import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '../services/authService.js'
import { usuarioApi } from '@/services/api/usuario'
import { getPlatform, getOrCreateDeviceId } from '@/services/device'
import { refreshAccessToken } from '@/services/tokenRefresh'
import { rememberMeStorage } from '@/services/rememberMe'

type LoginCredentials = {
  email: string
  password: string
  remember?: boolean
}

const USUARIO_ROLE = 'usuario'
const INVALID_USUARIO_ROLE_MESSAGE = 'Esta conta não possui acesso ao aplicativo do Usuário Bloop.'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))
  const user = ref<any>(null)
  const permissions = ref<string[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value)

  const isPasswordRecoveryPath = (path = '') =>
    path.includes('/forgot-password') || path.includes('/reset-password/')

  const login = async (credentials: LoginCredentials) => {
    loading.value = true
    error.value = null

    try {
      const response = await authService.login(credentials)
      const email = credentials.email.trim()

      if (response.success && response?.data?.token) {
        if (response.data.user?.role !== USUARIO_ROLE) {
          throw new Error(INVALID_USUARIO_ROLE_MESSAGE)
        }

        token.value = response.data.token
        refreshToken.value = response.data.refresh_token
        user.value = response.data.user
        permissions.value = response.data.permissions || []

        localStorage.setItem('auth_token', response.data.token)
        localStorage.setItem('refresh_token', response.data.refresh_token)
        if (email) {
          localStorage.setItem('user_email', email)
        }

        if (credentials.remember && email) {
          rememberMeStorage.save(email)
        } else {
          rememberMeStorage.clear()
        }

        // Registrar FCM token assim que o usuário logar
        // Não bloquear o fluxo de login (em alguns WebViews pode "pendurar")
        registerPushToken().catch(() => {})

        return response
      } else {
        throw new Error(response?.message || 'Resposta inválida no login')
      }
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  const register = async (userData: any) => {
    loading.value = true
    error.value = null

    try {
      const response = await authService.register(userData)

      if (response.success && response?.data?.token) {
        token.value = response.data.token
        refreshToken.value = response.data.refresh_token
        user.value = response.data.user
        permissions.value = response.data.permissions || []

        localStorage.setItem('auth_token', response.data.token)
        localStorage.setItem('refresh_token', response.data.refresh_token)
        if (userData?.email) {
          localStorage.setItem('user_email', userData.email)
        }

        // Não bloquear o fluxo (mesmo motivo do login)
        registerPushToken().catch(() => {})

        return response
      } else {
        const registerError = new Error(response?.message || 'Resposta inválida no registro') as Error & {
          errors?: Record<string, string[]>
          status?: number
        }
        registerError.errors = response?.errors || {}
        registerError.status = response?.status
        throw registerError
      }
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async (options: { redirect?: boolean } = {}) => {
    const shouldRedirect = options.redirect !== false
    loading.value = true

    try {
      await authService.logout()
    } catch (err) {
      console.error('Erro ao fazer logout:', err)
    } finally {
      token.value = null
      refreshToken.value = null
      user.value = null
      permissions.value = []
      error.value = null

      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_email')
      loading.value = false

      // Redirecionar para a página de login apropriada
      if (typeof window !== 'undefined' && shouldRedirect) {
        const currentPath = window.location.pathname
        if (isPasswordRecoveryPath(currentPath)) {
          console.log('[DeepLinkDebug][AuthStore] logout sem redirect em rota de recuperação', {
            currentPath
          })
          return
        }

        // Se estamos em uma rota do módulo /usuario, usar a página de login específica
        const loginRoute = currentPath.startsWith('/usuario')
          ? '/usuario/login'
          : '/login'
        window.location.href = loginRoute
      }
    }
  }

  const refreshAuthToken = async () => {
    const currentToken = localStorage.getItem('auth_token')
    if (!currentToken) {
      throw new Error('No token to refresh')
    }

    try {
      const result = await refreshAccessToken()

      if (result.success && result.token) {
        token.value = result.token
        return true
      } else {
        throw new Error(result.message || 'Failed to refresh token')
      }
    } catch (err) {
      logout()
      throw err
    }
  }

  const registerPushToken = async (tokenOverride?: string, deviceIdOverride?: string) => {
    try {
      const fcmToken = tokenOverride || localStorage.getItem('fcm_token')
      if (!fcmToken) {
        console.warn('[Push] Token FCM não encontrado. Verifique se o Firebase está configurado corretamente.')
        console.warn('[Push] SHA-256 do certificado adicionado ao Firebase Console?')
        return
      }

      const deviceId = deviceIdOverride || getOrCreateDeviceId()
      const email = localStorage.getItem('user_email') || user.value?.email || ''

      await usuarioApi.registerPushToken({
        fcm_token: fcmToken,
        device_id: deviceId,
        platform: getPlatform(),
        device_name: deviceId,
        app_type: 'bloop-brasil', // Identifica qual app está registrando
        email
      })
    } catch (err) {
      console.warn('Não foi possível registrar o token de push agora', err)
    }
  }

  const checkAuth = async () => {
    if (!token.value) {
      return false
    }

    try {
      const response = await authService.getUser()

      if (response.success) {
        user.value = response.data
        // Atualizar permissões se fornecidas
        if (response.data.permissions) {
          permissions.value = response.data.permissions
        }
        return true
      } else {
        await logout()
        return false
      }
    } catch (err) {
      console.error('Erro na verificação de autenticação:', err)
      await logout()
      return false
    }
  }

  const validateSession = async (): Promise<boolean | { valid: boolean; authError?: boolean }> => {
    if (!token.value) {
      return { valid: false, authError: true }
    }

    try {
      // Verificar se o token ainda é válido
      const response = await authService.validateToken()

      if (response.success) {
        return { valid: true }
      } else {
        // Token inválido, tentar renovar
        try {
          await refreshAuthToken()
          return { valid: true }
        } catch (refreshError: any) {
          // Verificar se é erro de autenticação ou de rede
          const isAuthError = refreshError?.response?.status === 401 ||
            refreshError?.response?.status === 403
          console.error('Erro ao renovar token:', refreshError)
          return { valid: false, authError: isAuthError }
        }
      }
    } catch (err: any) {
      // Verificar se é erro de autenticação (401/403) ou erro de rede/servidor
      const status = err?.response?.status
      const isAuthError = status === 401 || status === 403

      console.warn('[validateSession] Erro na validação:', {
        status,
        isAuthError,
        message: err?.message
      })

      // Retornar resultado detalhado para que o caller possa decidir o que fazer
      return { valid: false, authError: isAuthError }
    }
  }


  const hasPermission = (permission: string) => {
    if (!permissions.value || permissions.value.length === 0) {
      return false
    }
    return permissions.value.includes(permission)
  }

  const hasAnyPermission = (permissionList: string[]) => {
    if (!permissions.value || permissions.value.length === 0) {
      return false
    }
    return permissionList.some(permission => permissions.value.includes(permission))
  }

  const hasAllPermissions = (permissionList: string[]) => {
    if (!permissions.value || permissions.value.length === 0) {
      return false
    }
    return permissionList.every(permission => permissions.value.includes(permission))
  }

  const hasRole = (role: string) => {
    return user.value?.role === role
  }

  const hasAnyRole = (roleList: string[]) => {
    return roleList.includes(user.value?.role || '')
  }

  const clearError = () => {
    error.value = null
  }

  return {
    token,
    refreshToken,
    user,
    permissions,
    loading,
    error,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    login,
    register,
    logout,
    refreshAuthToken,
    registerPushToken,
    checkAuth,
    validateSession,
    clearError
  }
}, {
  persist: {
    key: 'auth-store',
    storage: localStorage,
    paths: ['user', 'permissions']
  }
})
